import { useEffect, useState } from 'react';
import { ApiError, type Api, type Offering, type OfferingKit, type OfferingVariant } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { ReadOnlyNote } from '../ui';

const MAX = 15;
/** Offerings saved before variants and kits existed have neither: start them empty. */
const norm = (o: Offering): Offering & { variants: OfferingVariant[]; kit: OfferingKit } => ({ ...o, variants: o.variants ?? [], kit: { faqs: o.kit?.faqs ?? [], objections: o.kit?.objections ?? [] } });
const slug = (name: string, taken: Set<string>): string => {
  const base = name.normalize('NFKD').replace(/[^\p{L}\p{N}]+/gu, '_').replace(/[^a-z0-9_]/giu, '').toLowerCase().replace(/^_+|_+$/gu, '').replace(/^[^a-z]+/u, '') || 'urun';
  let id = base.slice(0, 40);
  for (let i = 2; taken.has(id); i += 1) id = `${base.slice(0, 36)}_${i}`;
  return id;
};

/**
 * ADR-041: the products and services the agent may present, in priority order (first = presented first when it
 * fits). The AI proposes them from the business's knowledge; nothing is kept until the owner saves. Live after publishing.
 */
export const OfferingsPanel = ({ api, t, agentId, initial, onSaved }: { api: Api; t: T; agentId: string; initial: Offering[]; onSaved: () => void }) => {
  // ADR-071: without the agents permission the list is read-only (items still open to show their fields).
  const canEdit = useCan()(PERM.agents);
  const [list, setList] = useState<Offering[]>(initial.map(norm));
  const [open, setOpen] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<'save' | 'suggest' | null>(null);
  const [suggested, setSuggested] = useState<Offering[] | null>(null);
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string>();
  const [done, setDone] = useState(false);
  useEffect(() => { setList(initial.map(norm)); setDirty(false); }, [initial]);
  useEffect(() => {
    if (!done) return;
    const id = setTimeout(() => setDone(false), 6000);
    return () => clearTimeout(id);
  }, [done]);

  const change = (next: Offering[]) => { setList(next); setDirty(true); setDone(false); };
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j]!, next[i]!];
    change(next);
  };
  const edit = (i: number, patch: Partial<Offering>) => change(list.map((o, k) => (k === i ? { ...o, ...patch } : o)));
  const add = () => {
    const id = slug(t('offerings.new'), new Set(list.map((o) => o.id)));
    change([...list, norm({ id, name: t('offerings.new'), summary: '', benefits: [] })]);
    setOpen(id);
  };
  const valid = list.every((o) => o.name.trim() && o.summary.trim());

  const save = async () => {
    setBusy('save');
    setError(undefined);
    try {
      const opt = (x?: string) => (x?.trim() ? x.trim() : undefined);
      const clean = list.map(norm).map(({ fitsWhen, terms, variants, kit, ...o }) => ({
        ...o, name: o.name.trim(), summary: o.summary.trim(), benefits: o.benefits.map((b) => b.trim()).filter(Boolean).slice(0, 3),
        ...(opt(fitsWhen) ? { fitsWhen: opt(fitsWhen) } : {}), ...(opt(terms) ? { terms: opt(terms) } : {}),
        // Empty rows are dropped; a half-filled question or objection is not kept.
        variants: variants.filter((v) => v.name.trim()).slice(0, 12).map((v) => ({ id: v.id, name: v.name.trim(), ...(opt(v.summary) ? { summary: opt(v.summary) } : {}), ...(opt(v.terms) ? { terms: opt(v.terms) } : {}) })),
        kit: {
          faqs: kit.faqs.filter((f) => f.question.trim() && f.answer.trim()).slice(0, 10).map((f) => ({ question: f.question.trim(), answer: f.answer.trim() })),
          objections: kit.objections.filter((x) => x.objection.trim() && x.response.trim()).slice(0, 6).map((x) => ({ objection: x.objection.trim(), response: x.response.trim() })),
        },
      }));
      await api.saveSpec(agentId, { offerings: clean });
      setDirty(false);
      // Saved: the open item folds and a confirmation shows at the top of the tab.
      setOpen(null);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(null);
    }
  };
  const suggest = async () => {
    setBusy('suggest');
    setError(undefined);
    try {
      const r = await api.suggestOfferings(agentId);
      setSuggested(r.suggestions);
      setPick(new Set(r.suggestions.map((s) => s.id)));
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(null);
    }
  };
  const addPicked = () => {
    const taken = new Set(list.map((o) => o.id));
    const extra = (suggested ?? []).filter((s) => pick.has(s.id) && !taken.has(s.id)).slice(0, MAX - list.length);
    change([...list, ...extra]);
    setSuggested(null);
  };

  return (
    <div className="settings offerings">
      {done && <div className="banner ok" role="status">✓ {t('offerings.saved')}</div>}
      <div className="card">
        <div className="row-between">
          <div>
            <h2>{t('offerings.title')}</h2>
            <p className="muted small">{t('offerings.subtitle')}</p>
          </div>
          {canEdit && <button type="button" className="secondary" disabled={busy !== null} onClick={() => void suggest()}>{busy === 'suggest' ? t('offerings.suggesting') : `✦ ${t('offerings.suggest')}`}</button>}
        </div>
        <ul className="offer-rules small">
          <li>{t('offerings.rule.order')}</li>
          <li>{t('offerings.rule.outbound')}</li>
          <li>{t('offerings.rule.inbound')}</li>
          <li>{t('offerings.rule.truth')}</li>
        </ul>
        <ReadOnlyNote t={t} need={PERM.agents} />
      </div>

      {suggested && (
        <div className="card offer-suggest">
          <h3>{suggested.length ? t('offerings.found', { n: suggested.length }) : t('offerings.none')}</h3>
          {suggested.map((s) => (
            <label key={s.id} className="list-row clickable">
              <input type="checkbox" checked={pick.has(s.id)} onChange={() => setPick((p) => { const n = new Set(p); if (n.has(s.id)) n.delete(s.id); else n.add(s.id); return n; })} />
              <span className="grow"><span className="title">{s.name}</span><span className="sub"> — {s.summary}{s.terms ? ` · ${s.terms}` : ''}</span></span>
            </label>
          ))}
          <div className="row-between">
            <span className="hint">{t('offerings.suggest.hint')}</span>
            <span className="actions">
              <button type="button" className="link" onClick={() => setSuggested(null)}>{t('common.cancel')}</button>
              {suggested.length > 0 && <button type="button" className="primary" disabled={pick.size === 0} onClick={addPicked}>{t('offerings.addPicked', { n: pick.size })}</button>}
            </span>
          </div>
        </div>
      )}

      {list.length === 0 && !suggested && <div className="empty"><p>{t('offerings.empty')}</p></div>}
      <ol className="offer-list">
        {list.map((o, i) => (
          <li key={o.id} className={`offer ${open === o.id ? 'open' : ''}`}>
            <div className="offer-head">
              <span className="rank" aria-label={t('offerings.rank', { n: i + 1 })}>{i + 1}</span>
              <button type="button" className="offer-title" onClick={() => setOpen((x) => (x === o.id ? null : o.id))} aria-expanded={open === o.id}>
                <b>{o.name || '—'}</b><span className="muted small">{o.summary || t('offerings.summary.missing')}</span>
                <span className="offer-counts small">{t('offerings.counts', { v: norm(o).variants.length, q: norm(o).kit.faqs.length, x: norm(o).kit.objections.length })}</span>
              </button>
              {canEdit && (
                <span className="offer-move">
                  <button type="button" className="icon-btn" aria-label={t('offerings.up')} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button type="button" className="icon-btn" aria-label={t('offerings.down')} disabled={i === list.length - 1} onClick={() => move(i, 1)}>↓</button>
                </span>
              )}
            </div>
            {open === o.id && (
              <fieldset className="offer-body readonly-fieldset" disabled={!canEdit}>
                <label>{t('offerings.name')}<input value={o.name} maxLength={120} onChange={(e) => edit(i, { name: e.target.value })} /></label>
                <label>{t('offerings.summary')}<textarea rows={2} maxLength={300} value={o.summary} onChange={(e) => edit(i, { summary: e.target.value })} /><span className="hint">{t('offerings.summary.hint')}</span></label>
                <label>{t('offerings.benefits')}<textarea rows={3} maxLength={400} value={o.benefits.join('\n')} onChange={(e) => edit(i, { benefits: e.target.value.split('\n').slice(0, 3) })} /><span className="hint">{t('offerings.benefits.hint')}</span></label>
                <div className="form-grid">
                  <label>{t('offerings.fits')}<input value={o.fitsWhen ?? ''} maxLength={200} onChange={(e) => edit(i, { fitsWhen: e.target.value })} /></label>
                  <label>{t('offerings.terms')}<input value={o.terms ?? ''} maxLength={200} onChange={(e) => edit(i, { terms: e.target.value })} /></label>
                </div>
                <span className="hint">{t('offerings.terms.hint')}</span>
                <OfferingDetails api={api} t={t} agentId={agentId} offering={norm(o)} onChange={(patch) => edit(i, patch)} />
                {canEdit && <div><button type="button" className="link danger" onClick={() => { change(list.filter((_, k) => k !== i)); setOpen(null); }}>{t('offerings.remove')}</button></div>}
              </fieldset>
            )}
          </li>
        ))}
      </ol>
      {canEdit && (
        <>
          <div className="row-between">
            <button type="button" className="secondary" disabled={list.length >= MAX} onClick={add}>+ {t('offerings.add')}</button>
            <span className="actions">
              {error && <span className="banner error small">{t('error.generic')}</span>}
              {dirty && !valid && <span className="banner error small" role="alert">{t('offerings.incomplete', { names: list.filter((o) => !o.name.trim() || !o.summary.trim()).map((o, k) => o.name.trim() || String(k + 1)).join(', ') })}</span>}
              {dirty && valid && <span className="muted small">{t('offerings.unsaved')}</span>}
              <button type="button" className="primary" disabled={!dirty || !valid || busy !== null} onClick={() => void save()}>{busy === 'save' ? '…' : t('save')}</button>
            </span>
          </div>
          <p className="hint">{t('offerings.publishNote')}</p>
        </>
      )}
    </div>
  );
};

/**
 * ADR-044: an offering's variants (the concrete items inside it) and its kit (questions customers ask, objections),
 * editable by hand or filled with AI from the business's knowledge (suggestions are picked before they are added).
 */
const OfferingDetails = ({ api, t, agentId, offering, onChange }: {
  api: Api; t: T; agentId: string; offering: Offering & { variants: OfferingVariant[]; kit: OfferingKit }; onChange: (patch: Partial<Offering>) => void;
}) => {
  // ADR-071: read-only rows without the agents permission (no AI fill, no add or remove).
  const canEdit = useCan()(PERM.agents);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [sugg, setSugg] = useState<({ variants: OfferingVariant[] } & OfferingKit) | null>(null);
  const [pick, setPick] = useState<Set<string>>(new Set());
  const { variants, kit } = offering;
  const setV = (next: OfferingVariant[]) => onChange({ variants: next });
  const setK = (next: Partial<OfferingKit>) => onChange({ kit: { ...kit, ...next } });

  const fill = async () => {
    setBusy(true);
    setError(false);
    try {
      const r = await api.suggestOfferingKit(agentId, offering);
      setSugg(r.suggestion);
      setPick(new Set([...r.suggestion.variants.map((v) => `v:${v.id}`), ...r.suggestion.faqs.map((_, k) => `q:${k}`), ...r.suggestion.objections.map((_, k) => `x:${k}`)]));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  const addPicked = () => {
    if (!sugg) return;
    onChange({
      variants: [...variants, ...sugg.variants.filter((v) => pick.has(`v:${v.id}`))].slice(0, 12),
      kit: {
        faqs: [...kit.faqs, ...sugg.faqs.filter((_, k) => pick.has(`q:${k}`))].slice(0, 10),
        objections: [...kit.objections, ...sugg.objections.filter((_, k) => pick.has(`x:${k}`))].slice(0, 6),
      },
    });
    setSugg(null);
  };
  const toggle = (k: string) => setPick((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const total = sugg ? sugg.variants.length + sugg.faqs.length + sugg.objections.length : 0;

  return (
    <div className="offer-details">
      <div className="row-between">
        <div>
          <h4>{t('offerings.details.title')}</h4>
          <p className="hint">{t('offerings.details.hint')}</p>
        </div>
        {canEdit && <button type="button" className="secondary" disabled={busy} onClick={() => void fill()}>{busy ? t('offerings.suggesting') : `✦ ${t('offerings.details.fill')}`}</button>}
      </div>
      {error && <span className="banner error small">{t('error.generic')}</span>}
      {sugg && (
        <div className="offer-suggest card">
          <b>{total ? t('offerings.details.found', { n: total }) : t('offerings.details.none')}</b>
          {sugg.variants.map((v) => <label key={`v:${v.id}`} className="list-row clickable"><input type="checkbox" checked={pick.has(`v:${v.id}`)} onChange={() => toggle(`v:${v.id}`)} /><span className="grow"><span className="tag">{t('offerings.variant')}</span> {v.name}{v.summary ? ` — ${v.summary}` : ''}{v.terms ? ` · ${v.terms}` : ''}</span></label>)}
          {sugg.faqs.map((f, k) => <label key={`q:${k}`} className="list-row clickable"><input type="checkbox" checked={pick.has(`q:${k}`)} onChange={() => toggle(`q:${k}`)} /><span className="grow"><span className="tag">{t('offerings.faq')}</span> {f.question} → {f.answer}</span></label>)}
          {sugg.objections.map((x, k) => <label key={`x:${k}`} className="list-row clickable"><input type="checkbox" checked={pick.has(`x:${k}`)} onChange={() => toggle(`x:${k}`)} /><span className="grow"><span className="tag">{t('offerings.objection')}</span> {x.objection} → {x.response}</span></label>)}
          <div className="row-between">
            <span className="hint">{t('offerings.suggest.hint')}</span>
            <span className="actions">
              <button type="button" className="link" onClick={() => setSugg(null)}>{t('common.cancel')}</button>
              {total > 0 && <button type="button" className="primary" disabled={pick.size === 0} onClick={addPicked}>{t('offerings.addPicked', { n: pick.size })}</button>}
            </span>
          </div>
        </div>
      )}

      <div className="detail-block">
        <h5>{t('offerings.variants')} <span className="muted small">({variants.length})</span></h5>
        {variants.map((v, k) => (
          <div key={v.id} className="detail-row three">
            <input aria-label={t('offerings.variant.name')} placeholder={t('offerings.variant.name')} value={v.name} maxLength={120} onChange={(e) => setV(variants.map((x, j) => (j === k ? { ...x, name: e.target.value } : x)))} />
            <input aria-label={t('offerings.variant.summary')} placeholder={t('offerings.variant.summary')} value={v.summary ?? ''} maxLength={200} onChange={(e) => setV(variants.map((x, j) => (j === k ? { ...x, summary: e.target.value } : x)))} />
            <input aria-label={t('offerings.variant.terms')} placeholder={t('offerings.variant.terms')} value={v.terms ?? ''} maxLength={200} onChange={(e) => setV(variants.map((x, j) => (j === k ? { ...x, terms: e.target.value } : x)))} />
            {canEdit ? <button type="button" className="icon-btn" aria-label={t('offerings.row.remove')} onClick={() => setV(variants.filter((_, j) => j !== k))}>×</button> : <span />}
          </div>
        ))}
        {canEdit && <button type="button" className="link" disabled={variants.length >= 12} onClick={() => setV([...variants, { id: slug(`v ${variants.length + 1}`, new Set(variants.map((x) => x.id))), name: '' }])}>+ {t('offerings.variant.add')}</button>}
      </div>

      <div className="detail-block">
        <h5>{t('offerings.faqs')} <span className="muted small">({kit.faqs.length})</span></h5>
        {kit.faqs.map((f, k) => (
          <div key={k} className="detail-row two">
            <input aria-label={t('offerings.faq.q')} placeholder={t('offerings.faq.q')} value={f.question} maxLength={200} onChange={(e) => setK({ faqs: kit.faqs.map((x, j) => (j === k ? { ...x, question: e.target.value } : x)) })} />
            <textarea aria-label={t('offerings.faq.a')} placeholder={t('offerings.faq.a')} rows={2} value={f.answer} maxLength={400} onChange={(e) => setK({ faqs: kit.faqs.map((x, j) => (j === k ? { ...x, answer: e.target.value } : x)) })} />
            {canEdit ? <button type="button" className="icon-btn" aria-label={t('offerings.row.remove')} onClick={() => setK({ faqs: kit.faqs.filter((_, j) => j !== k) })}>×</button> : <span />}
          </div>
        ))}
        {canEdit && <button type="button" className="link" disabled={kit.faqs.length >= 10} onClick={() => setK({ faqs: [...kit.faqs, { question: '', answer: '' }] })}>+ {t('offerings.faq.add')}</button>}
      </div>

      <div className="detail-block">
        <h5>{t('offerings.objections')} <span className="muted small">({kit.objections.length})</span></h5>
        {kit.objections.map((x, k) => (
          <div key={k} className="detail-row two">
            <input aria-label={t('offerings.objection.q')} placeholder={t('offerings.objection.q')} value={x.objection} maxLength={200} onChange={(e) => setK({ objections: kit.objections.map((y, j) => (j === k ? { ...y, objection: e.target.value } : y)) })} />
            <textarea aria-label={t('offerings.objection.a')} placeholder={t('offerings.objection.a')} rows={2} value={x.response} maxLength={400} onChange={(e) => setK({ objections: kit.objections.map((y, j) => (j === k ? { ...y, response: e.target.value } : y)) })} />
            {canEdit ? <button type="button" className="icon-btn" aria-label={t('offerings.row.remove')} onClick={() => setK({ objections: kit.objections.filter((_, j) => j !== k) })}>×</button> : <span />}
          </div>
        ))}
        {canEdit && <button type="button" className="link" disabled={kit.objections.length >= 6} onClick={() => setK({ objections: [...kit.objections, { objection: '', response: '' }] })}>+ {t('offerings.objection.add')}</button>}
      </div>
    </div>
  );
};
